import React from 'react'
import StandardProductDashboard from './StandardProductDashboard'
import { CampaignTree } from '../../components/social-media/CampaignTree'
import { campaignsForQuery } from '../../components/social-media/campaignTreeModel'
import { useSkyCampaignTree } from '../../hooks/socialdash'
import useEntityFilterSummary from '../../hooks/useEntityFilterSummary'

const PRODUCT = 'all'

/**
 * All products — the admin view.
 *
 * It is the same arrangement as a department, and that is the design rather than
 * a shortcut: §0.3 settled that a product dashboard IS the admin dashboard plus
 * an enforced campaign filter. So the admin view is not a different page with
 * different code — it is that page with a campaign list that happens to be
 * everything, and a manifest `scope: selectable` that lets the sidebar narrow it.
 *
 * ⚠ THE EXTRA CONTROL ARRIVES BY COMPOSITION, WHICH IS THE WHOLE POINT.
 * `StandardProductDashboard` has no idea this page exists: it renders whatever
 * `sidebarExtra` returns. §4 forbids giving it a `variant` prop or a
 * per-product conditional, because the moment it has one, five departments'
 * opinions are entangled in one file and the cheap fork this structure protects
 * gets expensive.
 *
 * ⚠ Reachability is NOT this file's job and must not be added here. The audience
 * check lives in resolveSkyProduct + ListSkyProducts (T3.0/T3.1), server-side,
 * because a frontend route guard is a suggestion — the URL is typeable. The
 * campaign-tree endpoint refuses for every non-selectable product for the same
 * reason.
 */
export const AllProductsDashboard: React.FC = () => {
  const { groups, isLoading } = useSkyCampaignTree(PRODUCT)

  // 🔴 THE SELECTION AND THE WIRE VALUE ARE TWO DIFFERENT THINGS, and conflating
  // them is a real bug rather than a tidiness point. `campaignsForQuery`
  // collapses "everything ticked" to "no constraint" — so if that collapsed
  // value were also the checkbox state, ticking the last box would blank every
  // box on screen. The UI keeps the raw selection; only the query gets the
  // collapsed form. Exactly the split `platformSelection` / `platforms` already
  // makes, for exactly the same reason.
  const [selected, setSelected] = React.useState<string[]>([])

  // Entity counts per campaign, so a dead option is visible BEFORE it is
  // clicked rather than after — the same reasoning T3.5 gives for showing a
  // count beside each source. Shares the sidebar's cached summary request.
  const { campaigns: campaignFacets } = useEntityFilterSummary({
    customer: '',
    job: '',
    product: PRODUCT,
  })
  const counts = React.useMemo(() => {
    const out: Record<string, number> = {}
    for (const facet of campaignFacets) out[facet.value] = facet.count ?? 0
    return out
  }, [campaignFacets])

  return (
    <StandardProductDashboard
      product={PRODUCT}
      title="All products"
      subtitle="Every campaign and every data source, including comments we could not attribute to a campaign."
      /* 🔴 THE STANDARD PICKER IS OFF HERE BECAUSE THIS PAGE HAS ITS OWN
         (#1199). Departments got a flat multi-select in the filter bar; this
         view needs the campaigns GROUPED BY DEPARTMENT, which is what
         CampaignTree below is, and it keeps a raw selection alongside the
         collapsed value it puts on the wire (see the note on `selected`).

         Two controls over `filters.campaigns` would be the failure this
         codebase already refuses elsewhere: the bar would render the COLLAPSED
         value — empty whenever every box is ticked — beside a tree showing
         every box ticked, and the two would disagree with nothing on screen
         saying which was right. */
      campaignFilter={false}
      sidebarExtra={(filters) => (
        <CampaignTree
          groups={groups}
          isLoading={isLoading}
          counts={counts}
          selected={selected}
          onChange={(next) => {
            setSelected(next)
            // An explicit full list would still exclude any campaign the tree
            // does not know about — one added to Sprinklr since the manifest
            // was last edited, say — so ticking every box would show LESS than
            // ticking none. The collapse is what stops that.
            filters.setCampaigns(campaignsForQuery(groups, next))
          }}
          disabled={isLoading}
        />
      )}
    />
  )
}

export default AllProductsDashboard
